/**
 * Layer-import provenance — best-effort archival of the ORIGINAL uploaded
 * file (KMZ / shapefile zip / loose parts / GeoJSON / DWG) so imports can be
 * re-parsed when parsers improve, re-downloaded, and audited.
 *
 * Storage path (owner-scoped; satisfies the server's IDOR guard that
 * non-logo uploads live under the caller's own users/{uid}/ prefix):
 *
 *   users/{uid}/sites/{siteId}/layer-imports/{importId}/{fileName}
 *
 * Contract: NEVER blocks or fails a layer commit. Any error (offline, quota,
 * oversize) degrades to a console warning and a `null` return; the caller
 * stamps `importId`/`importFileName` attributes only when the upload
 * succeeded. Reuses the chunked endpoint for every size (a small file is
 * simply one chunk), through the shared `uploadRawFileChunked` protocol
 * helper — the single-shot endpoint's 413 ceiling never applies.
 */

import { authedFetch } from '../../lib/authedFetch';
import { uploadRawFileChunked } from '../knowledge/documentUpload';

/** Server-side resumable cap (CHUNKED_UPLOAD_MAX_BYTES in server.ts). */
const PROVENANCE_MAX_BYTES = 70 * 1024 * 1024;
const PROVENANCE_UPLOAD_TIMEOUT_MS = 120_000;

export interface LayerImportProvenance {
  importId: string;
  /** GCS object path of the archived original. */
  storagePath: string;
  fileName: string;
}

/** Keep the object key to a safe charset (no separators / control chars). */
function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9._ -]+/g, '_').replace(/\s+/g, ' ').trim();
  return cleaned !== '' ? cleaned.slice(0, 200) : 'import.bin';
}

/**
 * Archive one original upload. Returns the provenance record on success,
 * `null` on any failure (best-effort by contract).
 */
export async function uploadLayerImportProvenance(
  siteId: string,
  file: File,
  importId: string,
): Promise<LayerImportProvenance | null> {
  try {
    if (file.size <= 0 || file.size > PROVENANCE_MAX_BYTES) {
      return null;
    }
    const { auth } = await import('../../firebase');
    const uid = auth.currentUser?.uid;
    if (!uid) return null;

    const fileName = sanitizeFileName(file.name);
    const storagePath = `users/${uid}/sites/${siteId}/layer-imports/${importId}/${fileName}`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PROVENANCE_UPLOAD_TIMEOUT_MS);
    try {
      await uploadRawFileChunked(file, storagePath, authedFetch, controller.signal);
    } finally {
      clearTimeout(timer);
    }
    return { importId, storagePath, fileName };
  } catch (error) {
    console.warn('[layerImportProvenance] original-file archive failed (import still committed):', error);
    return null;
  }
}
